import { useState, useRef, useEffect } from 'react';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import AssignRoleModal from './assign_role_modal.component';
import { RoleAssignmentTable } from './RoleAssignmentTable';
import styles from './role_assignment.module.css';

import { createRoleAssignment } from './functions/create_role_assignment.function';
import { updateRoleAssignment } from './functions/update_role_assignment.function';
import { getAllRoles } from './functions/get_all_roles.function';
import { getAllUsers } from './functions/get_all_users.function';
import { getAllRoleAssignments } from './functions/get_all_role_assignments.function';
import { notify } from '../../common/functions/react-toastify.function';

const RoleAssignment = ({
  pageTitle = 'Invoice Approvers',
  submitForApproval = false,
  onSubmissionComplete,
  hidePageHeader = false,
  embedded = false,
  sectionTitle,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedRoleAssignment, setSelectedRoleAssignment] = useState(null);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [roleAssignments, setRoleAssignments] = useState([]);
  const tableRef = useRef(null);

  // Fetch roles, users, and role assignments in parallel on component load
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all three APIs in parallel
        const [rolesResult, usersResult, assignmentsResult] = await Promise.all([
          getAllRoles(),
          getAllUsers(),
          getAllRoleAssignments(),
        ]);

        // Process roles
        const responseArray = rolesResult?.response || rolesResult?.data || [];
        if (Array.isArray(responseArray) && responseArray.length > 0) {
          setRoles(responseArray);
        }

        // Process users
        const usersArray = usersResult?.response || usersResult?.data || [];
        if (Array.isArray(usersArray) && usersArray.length > 0) {
          setUsers(usersArray);
        }

        // Process role assignments
        const assignmentsArray = assignmentsResult?.response || assignmentsResult?.data || [];
        if (Array.isArray(assignmentsArray)) {
          setRoleAssignments(assignmentsArray);
        }
      } catch (error) {
        console.error('Error fetching data on component load:', error);
      }
    };

    fetchData();
  }, []);

  const handleAssignRole = () => {
    setSelectedRoleAssignment(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRoleAssignment(null);
  };

  const handleEditRoleAssignment = (roleAssignment) => {
    setSelectedRoleAssignment(roleAssignment);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleSubmit = async (roleAssignmentData) => {
    if (modalMode === 'create') {
      const result = await createRoleAssignment(roleAssignmentData);

      if (result.success) {
        const defaultMsg = submitForApproval
          ? 'Assignment change submitted for approval'
          : 'Role assigned successfully';
        const successMessage = result?.data?.message || defaultMsg;
        notify('success', successMessage);
        if (!submitForApproval && tableRef.current && result?.data?.response) {
          tableRef.current.addOrUpdateRoleAssignment(result.data.response, users, roles);
        }
        if (submitForApproval && onSubmissionComplete) {
          onSubmissionComplete();
        }
        return { success: true };
      } else {
        return { success: false };
      }
    } else {
      const result = await updateRoleAssignment(roleAssignmentData);

      if (result.success) {
        const defaultMsg = submitForApproval
          ? 'Assignment change submitted for approval'
          : 'Role assignment updated successfully';
        const successMessage = result?.data?.message || defaultMsg;
        notify('success', successMessage);
        if (!submitForApproval && tableRef.current && result?.data?.response) {
          tableRef.current.addOrUpdateRoleAssignment(result.data.response, users, roles);
        }
        if (submitForApproval && onSubmissionComplete) {
          onSubmissionComplete();
        }
        return { success: true };
      } else {
        return { success: false };
      }
    }
  };

  return (
    <div
      className={`${styles['role-assignment-container']} ${
        embedded ? styles['role-assignment-embedded'] : ''
      }`}
    >
      {!hidePageHeader && (
        <div className={styles['role-assignment-header']}>
          <div className={styles['header-left']}>
            <div className={styles['back-icon-container']}>
              <ArrowBackIosOutlinedIcon className={styles['back-icon']} />
            </div>
            <h1 className={styles['page-title']}>{pageTitle}</h1>
          </div>
          <button className={styles['assign-role-btn']} onClick={handleAssignRole}>
            Assign Role
          </button>
        </div>
      )}
      {hidePageHeader && (
        <div
          className={
            sectionTitle
              ? styles['invoice-approver-section-header-row']
              : styles['role-assignment-header']
          }
        >
          {sectionTitle ? (
            <h2 className={styles['invoice-approver-section-title']}>{sectionTitle}</h2>
          ) : (
            <div />
          )}
          <button className={styles['assign-role-btn']} onClick={handleAssignRole}>
            Assign Role
          </button>
        </div>
      )}

      <RoleAssignmentTable
        ref={tableRef}
        onEdit={handleEditRoleAssignment}
        onDeleteSuccess={() => {
          if (submitForApproval && onSubmissionComplete) {
            onSubmissionComplete();
          }
        }}
        submitForApproval={submitForApproval}
        users={users}
        roles={roles}
        roleAssignments={roleAssignments}
      />

      {/* Assign Role Modal */}
      <AssignRoleModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        mode={modalMode}
        initialData={selectedRoleAssignment}
        title={modalMode === 'create' ? 'Assign Role to User' : 'Edit Role Assignment'}
        buttonText={
          modalMode === 'create'
            ? submitForApproval
              ? 'Submit for approval'
              : 'Assign'
            : submitForApproval
              ? 'Submit for approval'
              : 'Update'
        }
        roles={roles}
        users={users}
      />
    </div>
  );
};

export default RoleAssignment;
